import React, { useState, useCallback, useEffect } from 'react';
import { Button, CardComponent } from '../../../components/shared';
import { Alert, Input } from 'antd';
import { authActions } from '../../../state/features/authReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

export interface ForgotPasswordProps {
    onComplete: () => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onComplete }) => {
    const { t } = useTranslation();
    const [username, setUserName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmationCode, setConfirmationCode] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [isConfirming, setIsConfirming] = useState<boolean>(false);
    
    const errorMessage = useSelector((x: RootState) => x.auth.error);
    const authLoading = useSelector((x: RootState) => x?.auth?.loading);
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

    const onSubmit = useCallback(async () => {
        if (username.length > 1) {
            if (!isConfirming) {
                onSubmitVerificationCode();
            } else if (password.length > 1 && confirmationCode.length > 1) {
                onRecoverPassword();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username, password, confirmationCode, isConfirming]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          onSubmit();
        }
    }, [onSubmit]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    
    const clickGate = Boolean(authLoading);

    async function onSubmitVerificationCode() {
        dispatch(authActions.forgotPassword({ username }));
        setIsConfirming(true);
    }

    async function onRecoverPassword() {
        dispatch(authActions.recoverPassword({ 
            username, 
            password, 
            code: confirmationCode.trim() 
        }));
        onComplete();
    }

    const toDisplayString = (): string => {
        return 'Reset Password';
    };

    const title = toDisplayString();
    
    return (
        <CardComponent title={title} className='login-card'>
           {errorMessage && <Alert className='alertText' message={errorMessage} type='error' />}
            <div className='fieldView'>
                <label className='labelView emaillable'>{t('auth.email')}</label>
                <Input
                    value={username}
                    placeholder={t('auth.email')}
                    onChange={(e) => {
                        setUserName(e.target.value);
                    }}
                    disabled={clickGate}
                />
            </div>
            {isConfirming && (
            <>
                <div className='fieldView'>
                    <label className='labelView'>{t('auth.newPassword')}</label>
                    <Input.Password
                        value={password}
                        placeholder={t('auth.password')}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={clickGate}
                    />
                </div>
                <div style={{ color: 'white' }}>
                    <label className='labelView emaillable'>{t('auth.verifyCode')}</label>
                    <Input
                        value={confirmationCode}
                        onChange={(e) => {
                            setConfirmationCode(e.target.value);
                        }}
                        disabled={clickGate}
                    />
                </div>
            </>
            )}
            
            {!isConfirming ? (
            <div style={{ marginTop: '15px' }}>
                <Button type='primary' onClickEvent={onSubmit}>
                    {t('auth.sendCode')}
                </Button>
            </div>
            ) :
            (
                <div className='resetBtn'>
                    <Button type='primary' onClickEvent={onSubmit}>
                        {t('auth.resetPassword')}
                    </Button>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button type='link' onClickEvent={() => onComplete()} className='forgetPassword'>
                    {t('general.cancel')}
                </Button>
            </div>
        </CardComponent>
    );
};
